/**
 * Feb 2026 · Shared "Cancel with reason" dialog.
 *
 * Ops-signed-off replacement for the physical Delete confirm on any
 * posted document (SO / PO / PI). Requires a reason ≥ 4 chars, submits
 * to the doc-specific `/cancel` endpoint, and never touches the row —
 * the backend flips `status="Cancelled"` and stamps who/when/why.
 *
 * Backend guarantees the audit trail is written by `AuditLogMiddleware`.
 */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { WarningIcon, XIcon, ProhibitIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const btnGhost = "inline-flex items-center gap-1.5 h-9 px-4 border border-[var(--border)] text-xs font-mono uppercase tracking-widest rounded-sm hover:bg-[var(--bg-2)] transition";

export default function CancelWithReasonDialog({ open, onOpenChange, docType, docNo, cancelPath, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const r = reason.trim();
    if (r.length < 4) { toast.error("Reason must be at least 4 characters"); return; }
    setSaving(true);
    try {
      await api.post(cancelPath, { reason: r });
      toast.success(`${docNo || docType} cancelled`);
      setReason("");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setReason(""); onOpenChange(o); }}>
      <DialogContent data-testid="cancel-dialog" className="bg-[var(--bg-1)] border-red-500/60 max-w-md" description="Cancel or short-close the document with a mandatory reason for the audit trail.">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <WarningIcon size={18} weight="bold" className="text-red-400" />
            Cancel {docType} {docNo && <span className="font-mono text-[var(--accent)]">· {docNo}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="text-[11px] text-zinc-400 leading-relaxed border-l-2 border-red-500/60 pl-3">
            Cancelling flips the status to <b className="text-red-300">Cancelled</b>, stamps who / when / why on the record,
            and preserves the audit trail. The row is <b>not</b> deleted.
          </div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Reason (≥ 4 characters) *</div>
            <textarea
              data-testid="cancel-reason"
              autoFocus
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Buyer cancelled the PO after price change"
              className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-red-400"
            />
          </label>
        </div>
        <DialogFooter className="pt-2">
          <button data-testid="cancel-abort" onClick={() => onOpenChange(false)} className={btnGhost} disabled={saving}>
            <XIcon size={12} weight="bold" />Keep {docType}
          </button>
          <button data-testid="cancel-confirm" onClick={submit} disabled={saving || reason.trim().length < 4}
            className="h-9 px-4 bg-red-600 hover:bg-red-500 text-white text-xs font-mono uppercase tracking-widest rounded-sm inline-flex items-center gap-1.5 disabled:opacity-40">
            <ProhibitIcon size={12} weight="bold" />{saving ? "Cancelling…" : `Cancel ${docType}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
